import React, { useCallback, useEffect, useRef, useState } from 'react';
import type {
  TableProps,
  TableHeaderProps,
  TableCellProps,
  SortDirection,
  PaginationProps,
} from './Table.types';

/**
 * Iconos para sort
 */
const ChevronUpIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="transition-transform"
  >
    <path
      d="M12 10L8 6L4 10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="transition-transform"
  >
    <path
      d="M4 6L8 10L12 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronUpDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6L8 2.5L12 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4 10L8 13.5L12 10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Iconos para paginación
 */
const ArrowLeftIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14 8H2M2 8L6.5 3.5M2 8L6.5 12.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 8H14M14 8L9.5 3.5M14 8L9.5 12.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Iconos de paginación (chevron simples y dobles) — mismos paths que el prototipo (lucide).
const ChevronLeftIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
);
const ChevronRightIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
);
const ChevronsLeftIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m11 17-5-5 5-5" /><path d="m18 17-5-5 5-5" /></svg>
);
const ChevronsRightIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 17 5-5-5-5" /><path d="m13 17 5-5-5-5" /></svg>
);

/**
 * TableHeader - Componente para el header de una columna
 */
const TableHeader = <T,>({
  column,
  sortable,
  sorted,
  sortDirection,
  onSort,
  stickyHeader = false,
  headerBand = false,
  dense = false,
}: TableHeaderProps<T>) => {
  // Clases base del header. En modo compacto (`dense`) el alto del header iguala EXACTO
  // el de las filas compactas (py-2 + min-h-[44px]) para un remate pixel-exact.
  const baseClasses = `
    flex
    items-center
    gap-3
    ${dense ? 'px-4 py-2 min-h-[44px]' : 'px-4 py-2.5 min-h-[40px]'}
    w-full
    border-b
    border-border-primary
    dark:border-dark-border-primary
    transition-colors
    duration-150
  `;

  // Clases según sortable
  const sortableClasses = sortable
    ? `
      cursor-pointer
      hover:bg-background-secondary
      dark:hover:bg-dark-bg-primary/50
      select-none
    `
    : '';

  // Clases de alineación del contenedor flex
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  // Clases de alineación interna del span de contenido del header
  // Necesario porque flex-1 ocupa todo el ancho y justify-* del padre no centra el contenido interno
  const spanAlignClasses = {
    left: '',
    center: 'flex justify-center items-center',
    right: 'flex justify-end items-center',
  };

  // "Banda de título": alto EXACTO del área útil de la celda (alto de fila menos el padding
  // vertical). El título se centra (items-center) dentro de esta banda, de modo que queda
  // perfectamente centrado en vertical cuando el header está cerrado; y como la banda se ancla
  // arriba, al abrir un filtro (que crece la fila) los títulos siguen alineados entre columnas.
  // dense: 44px - 2×8px(py-2) = 28px · normal: 40px - 2×10px(py-2.5) = 20px.
  const titleBand = dense ? 'min-h-[28px]' : 'min-h-[20px]';

  // Clases según stickyRight y/o stickyHeader (header fijo vertical).
  // Cuando stickyHeader está activo, el fondo del header pasa a #f8f9fc (tablehead del prototipo)
  // y debe ser opaco para cubrir el contenido al scrollear.
  // La BANDA (el fondo gris) y el FIJADO son cosas distintas y ya no viajan juntas:
  // `headerBand` pinta el gris del canon sin convertir la tabla en contenedor de
  // scroll, que es lo que `stickyHeader` arrastra consigo.
  const conBanda = stickyHeader || headerBand;
  let stickyClasses = '';
  if (column.stickyRight && stickyHeader) {
    // Esquina superior-derecha: fija en ambos ejes, por encima de todo.
    stickyClasses = 'sticky top-0 right-0 z-30 bg-[#f8f9fc] dark:bg-dark-bg-secondary shadow-[-2px_0_5px_rgba(0,0,0,0.05)]';
  } else if (column.stickyRight) {
    // Con banda, la celda pegada a la derecha tiene que llevar el MISMO gris: en
    // blanco se recortaba contra sus vecinas y parecía otra columna.
    stickyClasses = conBanda
      ? 'sticky right-0 z-10 bg-[#f8f9fc] dark:bg-dark-bg-secondary shadow-[-2px_0_5px_rgba(0,0,0,0.05)]'
      : 'sticky right-0 z-10 bg-white dark:bg-dark-bg-primary shadow-[-2px_0_5px_rgba(0,0,0,0.05)]';
  } else if (stickyHeader) {
    stickyClasses = 'sticky top-0 z-20 bg-[#f8f9fc] dark:bg-dark-bg-secondary';
  } else if (headerBand) {
    stickyClasses = 'bg-[#f8f9fc] dark:bg-dark-bg-secondary';
  }

  // Con stickyHeader (listado MasterCrud) las cabeceras se alinean ARRIBA (como el prototipo,
  // cuyos `th` son `align-top`): así, cuando una columna abre su filtro y su fila crece, los
  // títulos de las demás columnas no quedan centrados verticalmente respecto a ella.
  const combinedBase = stickyHeader ? baseClasses.replace('items-center', 'items-start') : baseClasses;

  const combinedClasses = [combinedBase, sortableClasses, stickyClasses, alignClasses[column.align || 'left']]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Header "crudo": el ReactNode controla todo su layout (título + filtro por columna),
  // sin el span truncante ni los iconos de orden internos. Alineado arriba (items-start)
  // para que, al abrir el filtro, la fila de título no se descentre respecto a las demás columnas.
  // El crecimiento de la fila al abrir un filtro lo resuelve el contenedor grid con
  // `auto-rows-min` (grid-auto-rows: min-content), de modo que TODAS las celdas de cabecera se
  // estiran a la altura del track y el fondo #f8f9fc forma una banda continua a lo ancho de la
  // tabla (como el prototipo), aunque solo la columna pulsada muestre su input.
  if (column.headerRaw) {
    const rawClasses = [baseClasses.replace('items-center', 'items-start'), stickyClasses]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    return (
      <div
        role="columnheader"
        className={rawClasses}
        // El ReactNode gobierna el LAYOUT de esta cabecera, pero la semántica de orden no es
        // layout. Sin estas tres líneas una columna `sortable` con cabecera cruda se queda sin
        // `aria-sort` y sin ninguna vía de teclado: el contenido crudo suele traer solo un `div`
        // con `onClick`, que el ratón alcanza y el teclado no (WCAG 2.1.1, nivel A).
        aria-sort={sorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
        tabIndex={sortable ? 0 : undefined}
        onKeyDown={
          sortable
            ? (e) => {
                // Solo si el foco está en la cabecera misma. Dentro del contenido crudo suele ir
                // el filtro por columna, y su Enter debe filtrar, no reordenar la tabla.
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSort();
                }
              }
            : undefined
        }
      >
        {column.header}
      </div>
    );
  }

  return (
    <div
      className={combinedClasses}
      onClick={sortable ? onSort : undefined}
      // `columnheader` siempre: el `button` que llevaba cuando es ordenable dejaba la
      // columna sin anunciarse como encabezado. La accion de ordenar se comunica con
      // `aria-sort` y el `tabIndex`, que ya estaban.
      role="columnheader"
      aria-sort={sorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined}
      tabIndex={sortable ? 0 : undefined}
      onKeyDown={
        sortable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSort();
              }
            }
          : undefined
      }
    >
      {/* Banda de título de alto = área útil: centra el texto en vertical pixel-exact */}
      <div className={`flex items-center gap-3 w-full ${titleBand}`}>
        {/* Texto del header */}
        <span
          className={`
            flex-1
            min-w-0
            text-sm
            font-semibold
            text-content-primary
            dark:text-dark-content-primary
            text-ellipsis
            overflow-hidden
            whitespace-nowrap
            ${spanAlignClasses[column.align || 'left']}
          `}
        >
          {column.header}
        </span>

        {/* Iconos de sort */}
        {sortable && (
          <span className={`shrink-0 transition-colors duration-150 ${
            sorted
              ? 'text-primary-custom-600 dark:text-primary-custom-600'
              : 'text-content-secondary dark:text-content-secondary opacity-40'
          }`}>
            {sorted && sortDirection === 'asc' && <ChevronUpIcon />}
            {sorted && sortDirection === 'desc' && <ChevronDownIcon />}
            {!sorted && <ChevronUpDownIcon />}
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * TableCell - Componente para una celda de datos
 */
const TableCell: React.FC<TableCellProps & {
  isStriped?: boolean;
  rowIndex?: number;
  isStickyRight?: boolean;
  className?: string;
  isSelected?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  clickable?: boolean;
  accentHover?: boolean;
  dense?: boolean;
}> = ({
  children,
  align = 'left',
  width,
  isStriped = false,
  rowIndex = 0,
  isStickyRight = false,
  className = '',
  isSelected = false,
  isFirst = false,
  isLast = false,
  clickable = false,
  accentHover = false,
  dense = false,
}) => {
  // Clases de alineación horizontal
  const alignClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  // Lógica de fondo: prioridad selección > zebra > base
  const bgClass = isSelected
    ? 'bg-[#f7fcff] dark:bg-primary-900/20'
    : isStriped && rowIndex % 2 === 1
      ? 'bg-background-secondary dark:bg-dark-bg-primary/30'
      : 'bg-white dark:bg-dark-bg-primary';

  // Clases para sticky column
  const stickyClasses = isStickyRight
    ? 'sticky right-0 z-10 shadow-[-2px_0_5px_rgba(0,0,0,0.05)]'
    : '';

  // Redondeo solo si está seleccionado
  const roundedClasses = isSelected
    ? `${isFirst ? 'rounded-l-lg' : ''} ${isLast ? 'rounded-r-lg' : ''}`
    : '';

  // Hover del prototipo (N2): fondo #f0f7ff en toda la fila + acento izquierdo #0066ff
  // en la PRIMERA celda (sin salto de layout: el borde ocupa 3px siempre, solo cambia el color).
  const hoverBgClass = !isSelected
    ? (accentHover
        ? 'group-hover:bg-[#f0f7ff] dark:group-hover:bg-primary-900/10'
        : 'group-hover:bg-[#f9fafb] dark:group-hover:bg-white/[0.02]')
    : '';
  // El acento reserva 3px SIEMPRE y solo cambia de color en hover (así no hay salto de
  // layout), pero para que el reposo sea transparente el color del separador inferior tiene
  // que pintarse POR LADO (ver el `border-b-border-secondary` de abajo):
  // `border-border-secondary` pinta `border-color` de los CUATRO lados y se comía este
  // `border-l-transparent`, dejando una franja gris fija de 3px en la primera celda
  // (medido en dev el 2026-08-21: rgb(244,244,245) en reposo Y en hover).
  //
  // PENDIENTE, fuera de este componente: el fondo de hover de la fila
  // (`group-hover:bg-[#f0f7ff]`, el `bg-rowhover` del prototipo) sigue sin aplicarse. Lo tapa
  // `master-pattern-view.css`, que redeclara ~98 utilidades genéricas —`.bg-white` entre
  // ellas— SIN capa de cascada: lo que va sin capa gana a lo que va en `@layer utilities`,
  // que es donde el consumidor emite sus variantes, por muy específicas que sean. Medido:
  // hover deja el fondo en rgb(255,255,255) en vez de rgb(240,247,255). Se arregla metiendo
  // ese bloque en `@layer utilities`, y no se hace aquí a propósito para no tocar
  // MasterPatternView.
  const accentBorderClass = accentHover && isFirst
    ? 'border-l-[3px] border-l-transparent group-hover:border-l-[#0066ff]'
    : '';

  return (
    <div
      role="cell"
      className={`
        flex
        items-center
        ${alignClasses[align]}
        ${dense ? 'px-4 py-2 min-h-[44px]' : 'px-4 py-4 min-h-[56px]'}
        w-full
        ${isSelected ? 'border-b-transparent' : 'border-b border-b-border-secondary dark:border-b-dark-border-primary'}
        ${bgClass}
        ${stickyClasses}
        ${roundedClasses}
        ${accentBorderClass}
        ${clickable ? 'cursor-pointer' : ''}
        ${hoverBgClass}
        transition-colors
        duration-150
        ${className}
      `}
    >
      <div
        className={`
          text-sm
          text-content-primary
          dark:text-dark-content-primary
          flex
          ${alignClasses[align]}
          w-full
        `}
      >
        {children}
      </div>
    </div>
  );
};


/**
 * Pagination - Componente de paginación
 */
const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showPageNumbers = false,
  previousLabel = 'Anterior',
  nextLabel = 'Siguiente',
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  totalRecords,
  perPageLabel = 'por pág.',
  recordsLabel = 'registros',
  goToPageLabel = 'Pág.',
  goToPageAriaLabel = 'Ir a página',
  firstPageLabel = 'Primera página',
  lastPageLabel = 'Última página',
}) => {
  /**
   * Lo escrito en el campo «ir a página», o `null` mientras nadie lo está editando.
   *
   * <para>`null` NO es lo mismo que cadena vacía: con `null` el campo muestra la página ACTUAL, y
   * con `''` muestra el campo vacío porque el usuario acaba de borrarlo. Antes el estado era `''`
   * en los dos casos y el campo quedaba permanentemente en blanco: la página actual sólo se
   * insinuaba como `placeholder`, que el navegador esconde en cuanto hay valor y que además no es
   * el dato, sino una pista. Con la barra en su forma por omisión —sin números de página, sólo
   * flechas— el usuario se quedaba sin ninguna forma de saber en qué página estaba.</para>
   */
  const [goToValue, setGoToValue] = useState<string | null>(null);

  const showExtras = !!(pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange && pageSize);
  const multiPage = totalPages > 1;

  // ===== LÓGICA DE NÚMEROS DE PÁGINA =====
  // Solo se pintan si el consumidor los pide con `showPageNumbers` (por defecto NO). Cuando se
  // piden: hasta 3 páginas se muestran todas; con más de 3, una ventana deslizante de 3 números
  // CENTRADA en la página actual. Primera/última se cubren con los botones de chevron doble,
  // así que no se usan puntos suspensivos.
  const MAX_NUMEROS_VISIBLES = 3;
  const getPageNumbers = (): number[] => {
    if (totalPages <= MAX_NUMEROS_VISIBLES) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    // La ventana se ancla para que quepan siempre 3 números sin desbordar por ninguno de los
    // dos extremos: pegada a 1 en las primeras páginas y a `totalPages` en las últimas.
    const start = Math.min(
      Math.max(currentPage - 1, 1),
      totalPages - (MAX_NUMEROS_VISIBLES - 1),
    );
    return Array.from({ length: MAX_NUMEROS_VISIBLES }, (_, i) => start + i);
  };

  const pageNumbers = getPageNumbers();

  // ===== IR A PÁGINA =====
  const handleGoTo = () => {
    const n = parseInt(goToValue ?? '', 10);
    if (!isNaN(n) && n >= 1 && n <= totalPages) onPageChange(n);
    // Vuelve a "nadie está editando", con lo que el campo pasa a mostrar la página vigente —la
    // nueva si el salto fue válido, la de antes si no—. Antes se vaciaba, y el usuario terminaba
    // el gesto con menos información en pantalla de la que tenía al empezarlo.
    setGoToValue(null);
  };

  // ===== TOKEN DE ESTILOS — idéntico para botones y controles =====
  // Todos los elementos interactivos de la barra comparten: color, peso, hover y transición.
  const interactiveBase = `
    text-sm font-semibold
    text-primary-600 dark:text-primary-600
    hover:bg-blue-50 dark:hover:bg-blue-900/20
    transition-colors duration-150
  `;

  // Botones de navegación (Anterior / Siguiente)
  const navBtnCls = `
    ${interactiveBase}
    flex items-center gap-1.5 px-3 py-1.5 rounded-md
    disabled:opacity-40 disabled:cursor-not-allowed
  `;

  // Número de página activo
  const pageActiveCls = `
    flex items-center justify-center min-w-[32px] px-2 py-1.5 rounded-md
    text-sm font-semibold text-primary-600 dark:text-primary-600
    bg-blue-100 dark:bg-blue-900/30
    transition-colors duration-150
  `;

  // Número de página inactivo
  const pageInactiveCls = `
    flex items-center justify-center min-w-[32px] px-2 py-1.5 rounded-md
    text-sm font-semibold text-primary-600 dark:text-primary-600
    hover:bg-blue-50 dark:hover:bg-blue-900/20
    transition-colors duration-150
  `;

  // Select e Input de la barra (prototipo): neutrales (borde `line`, texto `ink`, fondo blanco),
  // NO azules ni en negrita.
  const formControlCls = `
    text-sm text-content-primary dark:text-dark-content-primary
    bg-white dark:bg-dark-bg-primary
    border border-border-primary dark:border-dark-border-primary
    rounded py-1
    focus:outline-none focus:border-[#93d1fd]
    transition-colors
    disabled:opacity-40 disabled:cursor-not-allowed
    cursor-pointer
  `;

  // ===== SECCIÓN DE NAVEGACIÓN (compartida) — diseño del prototipo =====
  // Primera · Anterior · [números máx. 2] · Siguiente · Última (solo iconos, sin texto).
  const navIconBtnCls = `
    inline-flex items-center justify-center w-8 h-8 rounded-md cursor-pointer
    text-primary-600 dark:text-primary-600
    hover:bg-primary-100 dark:hover:bg-primary-900/20
    active:scale-95 transition-all duration-150
    disabled:opacity-40 disabled:cursor-not-allowed
  `.replace(/\s+/g, ' ').trim();
  const navNumBtnCls = (active: boolean) => `
    inline-flex items-center justify-center min-w-8 h-8 px-3 rounded-md cursor-pointer
    text-sm font-bold transition-all duration-150
    ${active
      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-600'
      : 'text-primary-600 dark:text-primary-600 hover:bg-primary-100 dark:hover:bg-primary-900/20 active:scale-95'}
  `.replace(/\s+/g, ' ').trim();

  const navSection = (
    <nav className="flex items-center gap-0.5" aria-label={previousLabel === 'Anterior' ? 'Paginación' : previousLabel}>
      <button type="button" title={firstPageLabel} aria-label={firstPageLabel} onClick={() => currentPage > 1 && onPageChange(1)} disabled={!multiPage || currentPage === 1} className={navIconBtnCls}>
        <ChevronsLeftIcon />
      </button>
      <button type="button" title={previousLabel} aria-label={previousLabel} onClick={() => currentPage > 1 && onPageChange(currentPage - 1)} disabled={!multiPage || currentPage === 1} className={navIconBtnCls}>
        <ChevronLeftIcon />
      </button>

      {showPageNumbers && multiPage && pageNumbers.map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => onPageChange(page)}
          aria-current={page === currentPage ? 'page' : undefined}
          className={navNumBtnCls(page === currentPage)}
        >
          {page}
        </button>
      ))}

      <button type="button" title={nextLabel} aria-label={nextLabel} onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)} disabled={!multiPage || currentPage === totalPages} className={navIconBtnCls}>
        <ChevronRightIcon />
      </button>
      <button type="button" title={lastPageLabel} aria-label={lastPageLabel} onClick={() => currentPage < totalPages && onPageChange(totalPages)} disabled={!multiPage || currentPage === totalPages} className={navIconBtnCls}>
        <ChevronsRightIcon />
      </button>
    </nav>
  );

  // ===== LAYOUT CON EXTRAS =====
  if (showExtras) {
    return (
      <div className="flex items-center justify-between w-full gap-4 flex-wrap">

        {/* IZQUIERDA — Selector de registros por página + total */}
        <div className="flex items-center gap-1.5 flex-none">
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange!(Number(e.target.value))}
            aria-label="Registros por página"
            className={`${formControlCls} px-2`}
          >
            {pageSizeOptions!.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          {/* En OSCURO los tres textos de esta barra van un paso más claros que su equivalente en
              claro: `dark-content-tertiary` (#71717A) sobre `dark-bg-secondary` (#27272A) da 3.08
              y no alcanza el 4.5 que WCAG AA exige para texto pequeño; `dark-content-secondary`
              (#A1A1AA) da 5.81. El TOKEN no se toca —lo usa medio kit—: sólo cambia cuál usa esta
              barra, y sólo en oscuro. */}
          <span className="text-sm text-content-tertiary dark:text-dark-content-secondary whitespace-nowrap">
            {perPageLabel}
          </span>
          {totalRecords !== undefined && (
            <>
              <span className="text-content-secondary dark:text-content-secondary select-none px-0.5">·</span>
              <span className="text-sm text-content-tertiary dark:text-dark-content-secondary whitespace-nowrap">
                {totalRecords.toLocaleString()} {recordsLabel}
              </span>
            </>
          )}
        </div>

        {/* CENTRO — Navegación */}
        {navSection}

        {/* DERECHA — Ir a página */}
        <div className="flex items-center gap-2 flex-none">
          <span className="text-sm text-content-tertiary dark:text-dark-content-secondary whitespace-nowrap">
            {goToPageLabel}
          </span>
          <input
            type="number"
            min={1}
            max={totalPages || 1}
            value={goToValue ?? String(currentPage)}
            onChange={(e) => setGoToValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleGoTo(); }}
            onBlur={handleGoTo}
            disabled={!multiPage}
            aria-label={goToPageAriaLabel}
            className={`${formControlCls} w-16 text-center px-2`}
          />
        </div>
      </div>
    );
  }

  // ===== LAYOUT ORIGINAL (sin extras — backward compatible) =====
  return (
    <div className="flex items-center justify-center w-full h-9">
      {navSection}
    </div>
  );
};

/**
 * Table - Componente principal de tabla
 *
 * Características implementadas:
 * - Definición de columnas flexible con accessor y render custom
 * - Sorting en columnas (controlado o no controlado)
 * - Variantes: basic, fullWidth, striped, grid
 * - Dark mode completo en todos los estados
 * - Estados de loading y empty
 * - Filas clickeables
 * - Accesibilidad con roles y keyboard navigation
 *
 * Mejores prácticas implementadas:
 * - Orden de modificadores: {responsive}:{dark}:{state}:{utility}
 * - Dark mode con estrategia 'class' (darkMode: 'class')
 * - Tokens de color consistentes con la documentación
 * - Type safety con TypeScript estricto y generics
 * - Accesibilidad con ARIA labels y keyboard support
 *
 * @see docs/colors.md - Sistema de colores
 * @see docs/typography.md - Sistema tipográfico (Label/Small para headers, Paragraph/Small para cells)
 * @see docs/spacing.md - Sistema de espaciado
 * @see docs/shadows.md - Sistema de sombras
 *
 * @example
 * ```tsx
 * // Uso básico
 * <Table
 *   title="Usuarios"
 *   columns={[
 *     { header: 'Nombre', accessor: 'name' },
 *     { header: 'Email', accessor: 'email' },
 *     { header: 'Rol', accessor: 'role', sortable: true },
 *   ]}
 *   data={users}
 * />
 *
 * // Con render custom
 * <Table
 *   columns={[
 *     {
 *       header: 'Estado',
 *       accessor: 'status',
 *       render: (value) => (
 *         <Badge color={value === 'active' ? 'green' : 'red'}>
 *           {value}
 *         </Badge>
 *       ),
 *     },
 *   ]}
 *   data={data}
 * />
 *
 * // Variante striped
 * <Table
 *   variant="striped"
 *   columns={columns}
 *   data={data}
 * />
 * ```
 */
export const Table = <T extends Record<string, any>>({
  title,
  columns,
  data,
  variant = 'basic',
  showBorder = true,
  showShadow = true,
  showPadding = true,
  onRowClick,
  onRowDoubleClick,
  disableRowTextSelection = false,
  suppressRowClickOnDoubleClick = false,
  doubleClickDelay = 250,
  onSort,
  sortColumn: externalSortColumn,
  sortDirection: externalSortDirection,
  fullWidth = false,
  emptyMessage = 'No hay datos disponibles',
  loading = false,
  loadingRows = 5,
  pagination,
  isRowSelected,
  className = '',
  id,
  stickyHeader = false,
  headerBand = false,
  minGridHeight,
  accentHover = false,
  dense = false,
}: TableProps<T>) => {
  // ===== ESTADO INTERNO DE SORT =====
  const [internalSortColumn, setInternalSortColumn] = useState<keyof T | string | null>(null);
  const [internalSortDirection, setInternalSortDirection] = useState<SortDirection>(null);

  // Usar sort externo si está controlado, sino usar interno
  const sortColumn = externalSortColumn !== undefined ? externalSortColumn : internalSortColumn;
  const sortDirection = externalSortDirection !== undefined ? externalSortDirection : internalSortDirection;

  // ===== MANEJAR SORT =====
  const handleSort = (columnAccessor: keyof T | string) => {
    let newDirection: SortDirection = 'asc';

    // Si ya está ordenado por esta columna, cambiar dirección
    if (sortColumn === columnAccessor) {
      if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else if (sortDirection === 'desc') {
        newDirection = null;
      }
    }

    // Si sort es controlado, llamar callback
    if (onSort) {
      onSort(columnAccessor, newDirection);
    } else {
      // Sino, actualizar estado interno
      setInternalSortColumn(newDirection ? columnAccessor : null);
      setInternalSortDirection(newDirection);
    }
  };

  // ===== INTERACCION DE FILA (CLIC / DOBLE CLIC) =====
  // `onRowDoubleClick` es opt-in: sin esa prop (y sin `disableRowTextSelection`) la fila se
  // comporta exactamente igual que antes —texto seleccionable incluido—, que es lo que necesitan
  // las tablas de consulta donde el usuario copia el contenido de una celda.
  //
  // Con la prop puesta, la fila deja de ser texto seleccionable. Sin eso el gesto real es inerte:
  // el segundo clic del doble clic selecciona la palabra bajo el cursor, y el guardia de seleccion
  // del `onClick` de abajo —que existe para no navegar cuando el usuario arrastra para copiar—
  // descarta el clic. Se midio en navegador real inyectando solo CSS: con `user-select: none` el
  // gesto funciona, al retirarlo vuelve a fallar.
  const hasRowClick = !!onRowClick;
  const hasRowDoubleClick = !!onRowDoubleClick;
  const isRowInteractive = hasRowClick || hasRowDoubleClick;
  // El doble clic aplaza el clic simple solo si el consumidor lo pide Y hay ambos callbacks: con
  // uno solo no hay nada que desambiguar y meter un timer cambiaria el momento de la invocacion.
  const deferRowClick = suppressRowClickOnDoubleClick && hasRowClick && hasRowDoubleClick;

  // `select-none` de Tailwind emite `-webkit-user-select: none` y `user-select: none`. La fila es
  // `display: contents`, pero `user-select` se hereda, así que llega igual a las celdas.
  const rowTextSelectionOff = hasRowDoubleClick || disableRowTextSelection;
  const rowSelectNoneClass = rowTextSelectionOff ? ' select-none' : '';

  // Timers pendientes del clic aplazado, por indice de fila. Se limpian al desmontar para no
  // invocar el callback del consumidor sobre un arbol que ya no existe.
  const pendingClickTimers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  useEffect(() => {
    const timers = pendingClickTimers.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const handleRowClick = useCallback(
    (row: T, rowIndex: number) => {
      if (!onRowClick) return;
      // Un click-drag para seleccionar/copiar el texto de una celda también dispara este "click"
      // al soltar el mouse. Si el usuario dejó una selección de texto activa, es que quería
      // copiar, no navegar al detalle.
      //
      // El guardia no aplica cuando la fila ya no es seleccionable: ahí no hay copia que proteger
      // y una selección residual de otra parte de la página no debe tragarse el clic (era
      // justamente lo que dejaba inerte el doble clic).
      if (!rowTextSelectionOff) {
        const selection = window.getSelection?.();
        if (selection && selection.toString().length > 0) return;
      }

      if (!deferRowClick) {
        onRowClick(row, rowIndex);
        return;
      }

      const timers = pendingClickTimers.current;
      const previous = timers.get(rowIndex);
      if (previous) clearTimeout(previous);
      timers.set(
        rowIndex,
        setTimeout(() => {
          timers.delete(rowIndex);
          onRowClick(row, rowIndex);
        }, doubleClickDelay),
      );
    },
    [onRowClick, deferRowClick, doubleClickDelay, rowTextSelectionOff],
  );

  const handleRowDoubleClick = useCallback(
    (row: T, rowIndex: number) => {
      if (!onRowDoubleClick) return;
      const timers = pendingClickTimers.current;
      const pending = timers.get(rowIndex);
      if (pending) {
        clearTimeout(pending);
        timers.delete(rowIndex);
      }
      onRowDoubleClick(row, rowIndex);
    },
    [onRowDoubleClick],
  );

  // Activación por teclado. `onRowClick` manda cuando existe (no cambia el comportamiento previo);
  // si `onRowDoubleClick` es el único callback, es él quien responde a Enter/Espacio, para que el
  // gesto no quede sin ruta accesible.
  const handleRowKeyActivate = useCallback(
    (row: T, rowIndex: number) => {
      if (onRowClick) {
        // El teclado no puede producir un doble clic: aquí no se aplaza nada.
        onRowClick(row, rowIndex);
        return;
      }
      onRowDoubleClick?.(row, rowIndex);
    },
    [onRowClick, onRowDoubleClick],
  );

  // ===== OBTENER VALOR DE CELDA =====
  const getCellValue = (row: T, accessor: keyof T | ((row: T) => any)) => {
    if (typeof accessor === 'function') {
      return accessor(row);
    }
    return row[accessor];
  };

  // ===== CLASES DEL CONTAINER =====
  // Con stickyHeader el container llena la altura disponible (flex-1 min-h-0) y sin gap,
  // para que el grid scrollee internamente y la paginación quede como footer fijo.
  const containerBaseClasses = `
    flex
    flex-col
    ${stickyHeader ? 'flex-1 min-h-0 gap-0' : 'gap-4'}
    bg-white
    dark:bg-dark-bg-primary
    rounded-xl
    transition-all
    duration-150
  `;

  const containerBorderClasses = showBorder
    ? 'border border-border-primary dark:border-dark-border-primary'
    : '';

  const containerShadowClasses = showShadow ? 'shadow-base' : '';

  const containerPaddingClasses = showPadding ? 'p-8' : 'p-0';

  const containerWidthClasses = fullWidth || variant === 'fullWidth' ? 'w-full' : '';

  const containerClasses = [
    containerBaseClasses,
    containerBorderClasses,
    containerShadowClasses,
    containerPaddingClasses,
    containerWidthClasses,
    className,
  ]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  // ===== CLASES DE LA TABLA =====
  const tableGridClasses = variant === 'grid'
    ? `
      border
      border-border-primary
      dark:border-dark-border-primary
      rounded-lg
      overflow-hidden
    `
    : '';

  // ===== RENDER =====
  return (
    <div className={containerClasses} id={id}>
      {/* Título opcional */}
      {title && (
        <div className="flex items-center justify-center w-full">
          <h3
            className="
              text-base
              font-semibold
              text-content-primary
              dark:text-dark-content-primary
              text-ellipsis
              overflow-hidden
              whitespace-nowrap
            "
          >
            {title}
          </h3>
        </div>
      )}

      {/* Tabla */}
      {/* Con stickyHeader el propio grid es el contenedor de scroll (flex-1 min-h-0 overflow-auto):
          el header (sticky top-0) se ancla a él y queda fijo, mientras la paginación —hermana
          fuera del scroll— permanece como footer fijo. Sin stickyHeader se conserva el
          comportamiento previo (overflow-x-auto). */}
      <div
        // La tabla se maqueta con CSS grid, no con <table>, así que sin estos roles
        // el árbol de accesibilidad la aplana a texto suelto: ni filas, ni encabezados
        // de columna. Verificado en Chrome que los roles SÍ se respetan aunque las
        // filas lleven `display: contents`, así que no hace falta cambiar la maqueta.
        role="table"
        className={`grid ${stickyHeader ? 'flex-1 min-h-0 overflow-auto grid-rows-[min-content] content-start auto-rows-min' : 'overflow-x-auto'} ${tableGridClasses}`}
        style={{
          display: 'grid',
          width: '100%',
          minWidth: '100%',
          // Suelo de alto de la caja de filas (ver `minGridHeight`). `content-start`
          // ya está en las clases del grid, así que las filas se quedan arriba y el
          // hueco sobra por abajo en vez de estirarlas.
          ...(minGridHeight ? { minHeight: minGridHeight, alignContent: 'start' } : {}),
          gridTemplateColumns: columns.map(c => {
            const width = c.width || '1fr';
            const widthStr = String(width);

            // Columna de ancho FIJO (opt-in): se queda exactamente en `width`, no se expande
            // para repartir el espacio sobrante (columnas utilitarias: acciones, selección, etc.).
            if (c.fixedWidth && c.width) {
              return /^\d+$/.test(widthStr) ? `${widthStr}px` : widthStr;
            }

            // Si el ancho no es flexible (fr, %) ni es 'auto',
            // lo transformamos en minmax para forzar expansión.
            if (!widthStr.includes('fr') && !widthStr.includes('%') && widthStr !== 'auto') {
              const finalWidth = /^\d+$/.test(widthStr) ? `${widthStr}px` : widthStr;
              return `minmax(${finalWidth}, 1fr)`;
            }
            return width;
          }).join(' '),
        }}
      >
        {/* Headers. El envoltorio va con `display: contents` para que las celdas sigan
            siendo hijas directas del grid y el dimensionado por `gridTemplateColumns` no
            cambie; solo aporta el `role="row"` que faltaba. */}
        <div role="row" className="contents">
        {columns.map((column, colIndex) => {
          const accessor = typeof column.accessor === 'function'
            ? `column_${colIndex}`
            : String(column.accessor);

          return (
            <TableHeader
              key={`header-${accessor}`}
              column={column}
              sortable={column.sortable || false}
              sorted={sortColumn === accessor}
              sortDirection={sortColumn === accessor ? sortDirection : null}
              onSort={() => handleSort(accessor)}
              stickyHeader={stickyHeader}
              headerBand={headerBand}
              dense={dense}
            />
          );
        })}
        </div>

        {/* Loading skeleton */}
        {loading && (
          <>
            {Array.from({ length: loadingRows }).map((_, rowIndex) => (
              columns.map((column, colIndex) => {
                const accessor = typeof column.accessor === 'function'
                  ? `column_${colIndex}`
                  : String(column.accessor);

                return (
                  <TableCell
                    key={`loading-${rowIndex}-${accessor}`}
                    align={column.align}
                    isStriped={variant === 'striped'}
                    rowIndex={rowIndex}
                    dense={dense}
                  >
                    <div
                      className="
                        h-4
                        w-full
                        bg-background-secondary
                        dark:bg-dark-border-primary
                        rounded
                        animate-pulse
                      "
                    />
                  </TableCell>
                );
              })
            ))}
          </>
        )}

        {/* Datos reales */}
        {!loading && data.length > 0 && (
          <>
            {data.map((row, rowIndex) => {
              const isSelected = isRowSelected?.(row, rowIndex);
              const isClickable = isRowInteractive;
              return (
                <div
                  key={`row-${rowIndex}`}
                  // `select-none` solo en las filas que lo piden (doble clic o la prop
                  // explícita): sin eso, la clase se llevaría por delante el copiar/pegar de
                  // las tablas que hoy no manejan el gesto.
                  className={`contents group${rowSelectNoneClass}`}
                  // `row` siempre. Cuando la fila es clicable el rol de boton la sacaba
                  // de la tabla para el lector de pantalla; el comportamiento de clic lo
                  // siguen dando el tabIndex y el onKeyDown de abajo.
                  role="row"
                  tabIndex={isClickable ? 0 : undefined}
                  onClick={
                    hasRowClick
                      ? () => handleRowClick(row, rowIndex)
                      : undefined
                  }
                  onDoubleClick={
                    hasRowDoubleClick
                      ? () => handleRowDoubleClick(row, rowIndex)
                      : undefined
                  }
                  onKeyDown={
                    isClickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleRowKeyActivate(row, rowIndex);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((column, colIndex) => {
                    const accessor = typeof column.accessor === 'function'
                      ? `column_${colIndex}`
                      : String(column.accessor);
                    const value = getCellValue(row, column.accessor);
                    const content = column.render
                      ? column.render(value, row, rowIndex)
                      : value;

                    return (
                      <TableCell
                        key={`cell-${rowIndex}-${accessor}`}
                        align={column.align}
                        isStriped={variant === 'striped'}
                        rowIndex={rowIndex}
                        isStickyRight={column.stickyRight}
                        isSelected={isSelected}
                        isFirst={colIndex === 0}
                        isLast={colIndex === columns.length - 1}
                        clickable={isClickable}
                        accentHover={accentHover}
                        dense={dense}
                      >
                        {content}
                      </TableCell>
                    );
                  })}
                </div>
              );
            })}
          </>
        )}

        {/* Estado empty */}
        {!loading && data.length === 0 && (
          <div
            className="
              flex
              items-center
              justify-center
              px-6
              py-12
              text-sm
              text-content-secondary
              dark:text-content-secondary
            "
            style={{ gridColumn: `1 / ${columns.length + 1}` }}
          >
            {emptyMessage}
          </div>
        )}
      </div>

      {/* Paginación */}
      {pagination && (
        <div className={`${stickyHeader ? 'flex-shrink-0 px-4 py-2 bg-[#f8f9fc] dark:bg-dark-bg-secondary border-t border-border-primary dark:border-dark-border-primary' : (!showPadding ? 'p-4 border-t border-border-secondary dark:border-dark-border-primary' : '')}`}>
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={pagination.onPageChange}
            showPageNumbers={pagination.showPageNumbers}
            previousLabel={pagination.previousLabel}
            nextLabel={pagination.nextLabel}
            firstPageLabel={pagination.firstPageLabel}
            lastPageLabel={pagination.lastPageLabel}
            pageSize={pagination.pageSize}
            pageSizeOptions={pagination.pageSizeOptions}
            onPageSizeChange={pagination.onPageSizeChange}
            totalRecords={pagination.totalRecords}
            perPageLabel={pagination.perPageLabel}
            recordsLabel={pagination.recordsLabel}
            goToPageLabel={pagination.goToPageLabel}
            goToPageAriaLabel={pagination.goToPageAriaLabel}
          />
        </div>
      )}
    </div>
  );
};
